import React, { type ReactNode, useCallback, useRef } from 'react';
import {
  TextField as TextFieldRAC,
  TextArea as TextAreaRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type TextAreaFieldProps } from './text-area-field-props';

export type TextAreaProps = {
  children: ReactNode;
};

export const TextArea = ({ children }: TextAreaProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    FieldLayout,
    hasError,
  } = useFieldPropsV2<TextAreaFieldProps>();

  const handleChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange],
  );

  const labelElement = findChildOfType(children, TextArea.Label);
  const inputElement = findChildOfType(children, TextArea.Input);
  const descriptionElement = findChildOfType(children, TextArea.Description);
  const errorElement = findChildOfType(children, TextArea.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={handleChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { id, placeholder } = useFieldPropsV2<TextAreaFieldProps>();

  const ref = useRef<HTMLTextAreaElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  return (
    <TextAreaRAC
      ref={ref}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      placeholder={placeholder}
      className={className}
    />
  );
};

TextArea.Label = Label;
TextArea.Input = Input;
TextArea.Description = Description;
TextArea.Error = Error;
